import { Graphics } from "@pixi/react";
import { BufferedAction } from "verdi-web-client/HelperFunctions";
import { sleep } from "verdi-common/utils/timeoutUtils";
import React from "react";
import { dayMs, minuteMs, secondMs } from "verdi-common/timeConstants";
import { EventHandler } from "verdi-web-client";
import { generateUID } from "verdi-common/utils/HelperFunctions";
import { HistoricalDataBase } from "verdi-web-client/Models/HistoricalData/HistoricalDataBase";

import GlobalOptions from "@/utils/global-options";
import { DynamicGraphContext } from "@/features/dynamic-graph/dynamic-graph-utility";

/**
 * Controls the minimum time delta (in ms) between data points that HistoricalDataBase
 * will keep separate rather than merging/compressing together. A smaller window preserves
 * short-lived spikes (e.g. brief pressure events) that would otherwise be averaged away.
 */
const POINT_MERGE_WINDOW_MS = secondMs * 5;

const SingleEventsBars = [];

const YValueTransformsPerDataType = {
    sensoterra: (v) => v / 100,
    irrometer: (v) => (100 - v) / 100,
};

function getYValueForDataType(dataType) {
    return YValueTransformsPerDataType[dataType.toLowerCase()];
}
const cachedDatabases = {
    key: {
        users: [],
        db: undefined,
        flaggedToDelete: false,
    },
};
const cleanDBCacheAction = new BufferedAction(
    () => {
        let subsequentCleanupRequired = false;
        Object.keys(cachedDatabases).forEach((key) => {
            if (cachedDatabases[key].users.length > 0) {
                cachedDatabases[key].flaggedToDelete = false;
            }
            if (cachedDatabases[key].users.length === 0) {
                subsequentCleanupRequired = true;
                if (
                    cachedDatabases[key].flaggedToDelete === false ||
                    cachedDatabases[key].flaggedToDelete === undefined
                ) {
                    cachedDatabases[key].flaggedToDelete = Date.now();
                } else if (Date.now() - cachedDatabases[key].flaggedToDelete.valueOf() > 15 * minuteMs) {
                    delete cachedDatabases[key];
                }
            }
        });
        const keysFlaggedForDeletion = Object.keys(cachedDatabases).filter(
            (key) => cachedDatabases[key].flaggedToDelete,
        );
        // Make sure we don't go above the max number of cached graph lines
        if (keysFlaggedForDeletion.length > GlobalOptions.maxCachedGraphLines) {
            keysFlaggedForDeletion
                .sort(
                    (a, b) =>
                        cachedDatabases[a].flaggedToDelete.valueOf() - cachedDatabases[b].flaggedToDelete.valueOf(),
                )
                .splice(GlobalOptions.maxCachedGraphLines)
                .forEach((key) => {
                    delete cachedDatabases[key];
                });
        }
        if (subsequentCleanupRequired) {
            cleanDBCacheAction.trigger();
        }
    },
    1000 * 30,
    false,
    true,
);

export default class TrendGraphLine extends React.Component {
    automationGraphicsObj = undefined;

    g = undefined;

    k = 0;

    /*
     * @type {HistoricalDataBase}
     */
    zoneHistoryDatabase;

    bufferedAction;

    constructor(props) {
        super(props);

        this.uid = generateUID("StaticMoistureLine");
        SingleEventsBars.push(this);
        this.state = {
            lineDataType: this.props.sensor.dataType,
            lineColor: `0x${this.props.sensor.color.substring(1)}`,
        };
        this.props.addToSensor({
            yAxisValueForDataType: (t) => this.zoneHistoryDatabase.data.getClosestPointToValue(t, "none"),
        });

        this.props.sensor.getYValueForDataType = (t) => {
            const nearestPoint = this.zoneHistoryDatabase.data.getClosestPointToValue(t, "none");
            return {
                nearestPoint: nearestPoint,
                pixelYValue: nearestPoint
                    ? this.optionsForGraphType.getYPercentage(
                          nearestPoint.getDeviceData(this.props.sensor.id)[this.state.lineDataType],
                      ) * this.props.height
                    : null,
            };
        };
        this.ownDBKey = `${this.props.sensor.id}__${this.state.lineDataType}__${this.props.sensor.uid || ""}`;
        if (!cachedDatabases[this.ownDBKey]) {
            cachedDatabases[this.ownDBKey] = {
                users: [],
                db: new HistoricalDataBase([this.props.sensor.id], [this.state.lineDataType], POINT_MERGE_WINDOW_MS),
                flaggedToDelete: new Date(0),
            };
            cachedDatabases[this.ownDBKey].db.acceptableZones = this.props.sensor.connectedZones;
        }
        this.zoneHistoryDatabase = cachedDatabases[this.ownDBKey].db;
        cachedDatabases[this.ownDBKey].users.push(this.uid);
        this.zoneHistoryDatabase.worryAboutPosition = false;
        cleanDBCacheAction.trigger();
    }

    componentDidMount() {
        if (!cachedDatabases[this.ownDBKey]) {
            cachedDatabases[this.ownDBKey] = {
                users: [],
                db: this.zoneHistoryDatabase,
                flaggedToDelete: new Date(0),
            };
        }
        if (!cachedDatabases[this.ownDBKey].users.includes(this.uid)) {
            cachedDatabases[this.ownDBKey].users.push(this.uid);
        }

        cleanDBCacheAction.trigger();
        setTimeout(() => {
            if (!cachedDatabases[this.ownDBKey].users.includes(this.uid)) {
                cachedDatabases[this.ownDBKey].users.push(this.uid);
            }
        }, 1000);
        let intitialLoadDelay = 10 + Math.random() * 20;
        if (Object.keys(this.context.lines).length > 20) {
            intitialLoadDelay = 10 + Math.random() * 800;
        } else if (Object.keys(this.context.lines).length > 5) {
            intitialLoadDelay = 10 + Math.random() * 200;
        }
        // staggers data loading
        setTimeout(() => {
            this.zoneHistoryDatabase
                .getData(new Date(Date.now() - 300 * dayMs), this.context.endTime)
                .then(() => {
                    this.redraw();
                })
                .catch((e) => {
                    console.warn("error fetching schedule history", e);
                });
            // load spreader v
        }, intitialLoadDelay);

        const action = async () => {
            await sleep(10 + Math.random() * 500);
            this.zoneHistoryDatabase
                .getData(new Date(this.context.startTime.valueOf()), new Date(Math.ceil(Date.now() / dayMs) * dayMs))
                .catch((e) => {
                    console.warn("error fetching schedule history", e);
                });
            this.zoneHistoryDatabase
                .updateMostRecentData()
                .then(() => {
                    this.redraw();
                })
                .catch((e) => {
                    console.warn("error fetching new data", e);
                });
        };

        const bufferedAction = new BufferedAction(action, 2000, true, false);
        this.bufferedAction = bufferedAction;
        if (this.optionsForGraphType) {
            this.optionsForGraphType.onCurrentRangeChange.addListener(() => {
                this.redraw();
            }, this.uid);
        }
        this.context.lines[this.props.sensor.uid].getNearestValue = (t) =>
            this.zoneHistoryDatabase.data.getClosestPointToValue(t, "none")?.getDeviceData(this.props.sensor.id)?.[
                this.state.lineDataType
            ];
        this.context.onDateRangeChange.addListener(() => {
            bufferedAction.trigger();
        }, this.uid);
        this.context.onFocusedLinesChanged.addListener(() => {
            this.redraw();
        }, this.uid);

        const associatedLineId = this.props.sensor.uid;
        this.context.appendRefreshToLine(associatedLineId, this.refreshData);
    }

    componentWillUnmount() {
        if (this.optionsForGraphType && this.optionsForGraphType.requiredRanges[this.props.sensor.uid]) {
            delete this.optionsForGraphType.requiredRanges[this.props.sensor.uid];
        }
        EventHandler.disposeOfAllHooksForUID(this.uid);
        this.context.unRegisterLine(this.uid);
        if (cachedDatabases[this.ownDBKey]) {
            cachedDatabases[this.ownDBKey].users = cachedDatabases[this.ownDBKey].users.filter((u) => u === this.uid);
        }
        cleanDBCacheAction.trigger();
    }

    get optionsForGraphType() {
        if (this.context.staticGraphDictionary[this.props.sensor.sensorType]) {
            return this.context.staticGraphDictionary[this.props.sensor.sensorType];
        }
        this.context.addGraphType(this.props.sensor.sensorType);

        return this.context.staticGraphDictionary[this.props.sensor.sensorType];
    }

    refreshData = () => {
        try {
            this.bufferedAction.trigger();
        } catch (e) {
            console.error(`Error refreshing data for ${this.props.sensor.id}`, e);
        }
    };

    _doRedraw() {
        if (!this.g) {
            return;
        }
        this.k++;
        // smoothingVarianceThreshold is the expected variance between similar data points. When smoothing the graph,
        // datapoints that are different by more than this amount will not be used.
        // default is zero
        const smoothingVarianceThreshold = this.optionsForGraphType.smoothingVarianceThreshold;
        const dataKey = this.zoneHistoryDatabase.getDataKeyForModel(this.props.sensor.id, this.state.lineDataType);
        const interpolation = this.optionsForGraphType.interpolation || "linear";
        const maxInterpolationTime = this.optionsForGraphType.maxInterpolationTime || 1000 * dayMs;

        /**
         * The bin size for creating a rough histogram to figure out what range needs to be shown
         * @type {number}
         */
        const binSizeForMinMax =
            Math.abs(this.optionsForGraphType.typicalRange[1] - this.optionsForGraphType.typicalRange[0]) / 10 || 10;
        try {
            this.g.clear();
            this.g.endFill();

            const valueBins = {};
            let totalPointCount = 0;
            let minPoint;
            let maxPoint = 0;
            // Return y value on canvas based on raw value from sensor
            const valueFromNumber = (number) => {
                if (number || number === 0) {
                    totalPointCount++;
                    const binnedNumber = Math.round(number / binSizeForMinMax) * binSizeForMinMax;
                    valueBins[binnedNumber] = (valueBins[binnedNumber] || 0) + 1;
                    minPoint = Math.min(number, minPoint === undefined ? number : minPoint);
                    if (maxPoint === undefined || number > maxPoint) {
                        maxPoint = number;
                    }
                }
                if (this.optionsForGraphType) {
                    return this.optionsForGraphType.getYPercentage(number) * this.props.height;
                }
                return getYValueForDataType(this.props.sensor.sensorType)(number) * this.props.height;
            };

            const focused = this.context.lines?.[this.props.sensor.uid]?.focused;

            if (this.props.sensor.automationRange && this.automationGraphicsObj && focused) {
                // since some sensors are inverted (tensiometers), we need to make sure that
                // we use the smaller and larger of the two values based on their actual y positon.
                const twoThresholds = [
                    valueFromNumber(this.props.sensor.automationRange[0]),
                    valueFromNumber(this.props.sensor.automationRange[1]),
                ];
                const topY = this.props.height - Math.min(...twoThresholds);
                const botY = this.props.height - Math.max(...twoThresholds);

                const drawAutomationRectangle = ({ yPosition, fillColor, lineColor, isBottom = false }) => {
                    const FULL_WIDTH = this.props.width;
                    const LINE_WIDTH = 2;
                    const LINE_ALPHA = 1;

                    // Calculate rectangle dimensions
                    const rectY = isBottom ? 0 : yPosition;
                    const rectHeight = isBottom ? yPosition : this.props.height - yPosition;

                    // Draw filled rectangle
                    this.automationGraphicsObj.beginFill(fillColor);
                    // line style has to be in different hex format for whatever reason
                    this.automationGraphicsObj.lineStyle(fillColor.toString(16).toUpperCase().padStart(6, "0"));
                    this.automationGraphicsObj.drawRect(0, rectY, FULL_WIDTH, rectHeight);

                    // Draw border line
                    this.g.moveTo(0, yPosition);
                    this.g.lineStyle(LINE_WIDTH, lineColor, LINE_ALPHA);
                    this.g.lineTo(FULL_WIDTH, yPosition);
                };

                // Initialize graphics object
                this.automationGraphicsObj.clear();
                this.automationGraphicsObj.zIndex = -100;

                // Draw blue rectangle (bottom area)
                drawAutomationRectangle({
                    yPosition: botY,
                    fillColor: 0xd3eafd,
                    lineColor: "#6bbcff",
                    isBottom: true,
                });

                // Draw orange rectangle (top area)
                drawAutomationRectangle({
                    yPosition: topY,
                    fillColor: 0xffe0b2,
                    lineColor: "#ee9e1f",
                    isBottom: false,
                });

                this.automationGraphicsObj.endFill();
            } else if (this.automationGraphicsObj) {
                this.automationGraphicsObj.clear();
            }
            this.g.beginFill(0xffffff, 0);
            let lineColor = this.state.lineColor;
            let lineWidth = 2.0;
            this.g.zIndex = 2;
            if (!focused) {
                lineColor = 0xa6a6a6;
                this.g.zIndex = 50;
                lineWidth = 0.5;
            } else {
                this.g.zIndex = 100;
            }
            this.g.lineStyle(lineWidth, lineColor, 1);

            const startOfGraph = this.context.startTime;
            const endOfGraph = this.context.endTime;
            const rangeOfGraph = endOfGraph.valueOf() - startOfGraph.valueOf();

            const getXOfDate = (date) => ((date.valueOf() - startOfGraph.valueOf()) / rangeOfGraph) * this.props.width;

            let finalDataPoint;
            if (this.zoneHistoryDatabase.data.value.length > 0) {
                finalDataPoint = this.zoneHistoryDatabase.data.value[this.zoneHistoryDatabase.data.value.length - 1];
            }
            // Initialize the first data point
            /**
             * The data point we are going to draw next
             * @type {DataPoint}
             */
            let nextDataPoint = this.zoneHistoryDatabase.data.getClosestPointToValue(endOfGraph.valueOf(), "higher");
            // If there is no point that is just past the end of the graph, use the most recent data point.
            if (!nextDataPoint && finalDataPoint) {
                nextDataPoint = finalDataPoint;
            }
            if (!nextDataPoint) {
                this.g.endFill();
                return;
            }

            const leadingData = [];
            const trailingData = [];
            let trailingPoint = nextDataPoint;
            let v = nextDataPoint.getDeviceData(this.props.sensor.id)[this.state.lineDataType];
            let lastX;
            let lastDate = nextDataPoint.date;

            this.g.moveTo(
                getXOfDate(nextDataPoint.date),
                this.props.height -
                    valueFromNumber(nextDataPoint.getDeviceData(this.props.sensor.id)[this.state.lineDataType]),
            );
            const dataForCSV = [];
            if (nextDataPoint) {
                dataForCSV.push(nextDataPoint.data);
            }
            let continueDrawing = true;

            const incrementCurrentPoint = () => {
                leadingData.unshift({
                    date: nextDataPoint.date,
                    value: nextDataPoint.data[dataKey],
                });
                while (leadingData.length > 9) {
                    leadingData.pop();
                }
                nextDataPoint = nextDataPoint.prevPoint;
                while (nextDataPoint && nextDataPoint.data[dataKey] === undefined) {
                    nextDataPoint = nextDataPoint.prevPoint;
                }
                if (nextDataPoint) {
                    v = nextDataPoint.data[dataKey];
                    dataForCSV.push(nextDataPoint.data);
                }
                trailingData.pop();
                while (trailingData.length < 9 && trailingPoint.prevPoint) {
                    trailingData.unshift({
                        date: trailingPoint.date,
                        value: trailingPoint.data[dataKey],
                    });
                    trailingPoint = trailingPoint.prevPoint;
                    while (trailingPoint && trailingPoint.data[dataKey] === undefined) {
                        trailingPoint = trailingPoint.prevPoint;
                    }
                }
            };
            while (nextDataPoint && continueDrawing) {
                if (!nextDataPoint) {
                    break;
                }
                // If this point is outside the frame, we should still draw this one, but exit the loop after this point.
                // graph display frame:✓
                //   x  ✓ |  ✓ ✓ ✓   ✓   ✓  ✓ | ✓
                //      ^ we include this point, but don't want to draw back any further
                if (nextDataPoint.date.valueOf() < startOfGraph) {
                    continueDrawing = false;
                }
                let dataPointValue = v;
                const dataPointDate = nextDataPoint.date.valueOf();
                let totalWeight = 1;

                // This handles smoothing. smoothingVarianceThreshold represents the amount of run-to-run variance we expect from a sensor,
                // and therefore what datapoints should be considered "close enough" to matter for data smoohting.
                // data points that are outside run-to-run variance will be skipped when smoothing
                // if smoothingVarianceThreshold is zero, then no points will be included when smoothing so we just skip the following code.
                if (smoothingVarianceThreshold > 0) {
                    /*
                     * this essentially calculates the weighted average of data points near the current one,
                     * where the weight of each data point decreases quadratically the further you get from the
                     * current point.
                     */

                    // keep track of our total
                    let runningTotal = dataPointValue;

                    // keeps track of what we need to divide the sum of all the data points we are averaging by
                    totalWeight = 1;

                    // this is how much influence the next data point should have on the position of the smoothed data point.
                    // every time we use another data point for smoothing, this weightOfNextPoint is reduced.
                    let weightOfNextPoint = 0.97;
                    for (let i = 0; i < Math.min(leadingData.length, trailingData.length); i++) {
                        const leadIndex = i;
                        const trailIndex = trailingData.length - 1 - i;
                        if (
                            Math.abs(leadingData[leadIndex].value - v) < smoothingVarianceThreshold &&
                            Math.abs(trailingData[trailIndex].value - v) < smoothingVarianceThreshold
                        ) {
                            weightOfNextPoint = weightOfNextPoint * weightOfNextPoint * 0.97;
                            runningTotal += leadingData[leadIndex].value * weightOfNextPoint;
                            runningTotal += trailingData[trailIndex].value * weightOfNextPoint;

                            // add 2x the weightOfNextPoint becuase we are adding 2 points to the running total.
                            totalWeight += weightOfNextPoint * 2;
                        } else {
                            break;
                        }
                    }

                    // use the weight and running total to figure out the smoothed value:
                    dataPointValue = runningTotal / totalWeight;
                }

                const x = getXOfDate(new Date(dataPointDate));
                const value = valueFromNumber(dataPointValue);
                const y = this.props.height - value;

                /**
                 * This is for the supplemental graph lines thing that allows you to add extra lines to a zone graph.
                 * It allows the date barriers at the start and end time to work correctly.
                 */
                let withinSensorActiveDateRange = true;
                if (this.props.sensor.startDate && this.props.sensor.startDate.valueOf() > dataPointDate) {
                    withinSensorActiveDateRange = false;
                }
                if (this.props.sensor.endDate && this.props.sensor.endDate.valueOf() < dataPointDate) {
                    withinSensorActiveDateRange = false;
                }

                if (
                    withinSensorActiveDateRange &&
                    Math.abs(lastDate.valueOf() - dataPointDate.valueOf()) < maxInterpolationTime
                ) {
                    if (interpolation === "linear") {
                        this.g.lineTo(x, y);
                    } else if (interpolation === "square") {
                        if (lastX !== undefined) {
                            this.g.lineTo(lastX, y);
                        }
                        this.g.lineTo(x, y);
                    }
                } else {
                    this.g.moveTo(x, y);
                }

                lastX = x;
                lastDate = dataPointDate;
                incrementCurrentPoint();
                if (!nextDataPoint) {
                    break;
                }
            }

            // If we are drawing a supplemental data stream, and the data stream ends due to being configured
            // to end at a specific date, we should draw a little X so that we can see that this is intentional
            // and not a strange bug.
            if (
                this.props.sensor.endDate &&
                this.props.sensor.endDate.valueOf() < endOfGraph.valueOf() &&
                this.props.sensor.endDate.valueOf() > startOfGraph.valueOf()
            ) {
                /**
                 * The data point where we are going to draw the termination. Possibly undefined because data may not
                 * exist past the termination. It looks wrong that it's getting the next data point "after" the cutoff,
                 * but this is because we actually draw in that datapoint anyway. So sort of a two wrongs make a right
                 * situation. It's not worth fixing it.
                 * @type {DataPoint}
                 */
                const lastDataPoint = this.zoneHistoryDatabase.data.getClosestPointToValue(
                    this.props.sensor.endDate,
                    "higher",
                );
                if (lastDataPoint) {
                    // figure out where to draw the X
                    const coords = [
                        getXOfDate(new Date(lastDataPoint.date.valueOf())),
                        this.props.height - valueFromNumber(lastDataPoint.data[dataKey]),
                    ];

                    // draw the X
                    this.g.moveTo(...coords);
                    drawSupplementalDataTerminator(this.g, ...coords);
                }
            }

            this.context.filteredGraphPoints[this.props.sensor.uid] = {
                id: this.props.sensor.uid,
                name: this.context.lines[this.props.sensor.uid].name,
                sensorType: this.context.lines[this.props.sensor.uid].sensorType,
                dataType: this.context.lines[this.props.sensor.uid].dataType,
                data: dataForCSV,
            };

            this.g.endFill();
            if (
                this.optionsForGraphType &&
                (!this.optionsForGraphType.requiredRanges[this.props.sensor.uid] ||
                    this.optionsForGraphType.requiredRanges[this.props.sensor.uid].toString() !==
                        [minPoint, maxPoint].toString())
            ) {
                const boundaries = getBoundariesFromHistogram(valueBins, totalPointCount, binSizeForMinMax, {
                    min: minPoint,
                    max: maxPoint,
                });

                if (!boundaries) {
                    this.optionsForGraphType.requiredRanges[this.props.sensor.uid] = [minPoint, maxPoint];
                } else {
                    this.optionsForGraphType.requiredRanges[this.props.sensor.uid] = boundaries;
                }
                this.optionsForGraphType.triggerPotentialYAxisChange.trigger();
            }
        } catch (e) {
            console.warn(e);
        }
    }

    redraw(g = undefined) {
        if (g) {
            this.g = g;
        }
        if (!this.g) {
            return;
        }
        this.k++;
        const temp = this.k;
        window.requestAnimationFrame(() => {
            if (temp !== this.k) {
                return;
            }
            this._doRedraw();
        });
    }

    render() {
        return (
            <>
                <Graphics
                    y={this.props.y || 0}
                    draw={(g) => {
                        this.redraw(g);
                    }}
                    options={{
                        interactive: this.props.interactive,
                    }}
                />
                {this.props.sensor.automationRange && (
                    <Graphics
                        y={this.props.y || 0}
                        draw={(g) => {
                            this.automationGraphicsObj = g;
                            this.redraw();
                        }}
                        options={{
                            interactive: false,
                        }}
                    />
                )}
            </>
        );
    }
}

/**
 * Draws an X where the data ends due to being intentionally cut off
 * @param g
 * @param x
 * @param y
 */
function drawSupplementalDataTerminator(g, x, y, size = 5) {
    g.moveTo(x - size, y - size);
    g.lineTo(x + size, y + size);

    g.moveTo(x - size, y + size);
    g.lineTo(x + size, y - size);

    g.moveTo(x, y);
}

function getBoundariesFromHistogram(histogram, totalPointCount, binSize, options) {
    const allBins = Object.keys(histogram).map((k) => parseFloat(k));
    allBins.sort((a, b) => a - b);
    const binCount = allBins.length;
    if (binCount < 2 || totalPointCount < 10) {
        return undefined;
    }

    const numPointsToIgnore = Math.round(5 + totalPointCount * 0.01);
    let pointsSeenSoFar = 0;
    let index = -1;
    while (pointsSeenSoFar < numPointsToIgnore && index < binCount) {
        index++;
        pointsSeenSoFar += histogram[allBins[index]];
    }
    let min = allBins[index] - binSize / 2;
    index = -1;
    pointsSeenSoFar = 0;
    while (pointsSeenSoFar < numPointsToIgnore && index < binCount) {
        index++;
        pointsSeenSoFar += histogram[allBins[binCount - index - 1]];
    }
    let max = allBins[binCount - index - 1] + binSize / 2;

    if (max > min) {
        const range = max - min;
        const padding = Math.max(range * 0.15, binSize * 2);

        if (options.min !== undefined && Math.abs(min - options.min) < padding * 2) {
            min = options.min;
        } else {
            min -= padding;
        }
        if (options.max !== undefined && Math.abs(max - options.max) < padding * 2) {
            max = options.max;
        } else {
            max += padding;
        }
        return [min, max];
    }
    return undefined;
}

TrendGraphLine.contextType = DynamicGraphContext;

// TrendGraphLine.propTypes = {
//     height: PropTypes.number,
//     interactive: PropTypes.bool,
//     sensor: PropTypes.object,
//     endTime: PropTypes.instanceOf(Date),
//     startTime: PropTypes.instanceOf(Date),
//     width: PropTypes.number,
//     addToSensor: PropTypes.func,
// };
